import { useState, useCallback, useRef } from 'react';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

interface CompressResult {
	data: Uint8Array;
	blob: Blob;
	url: string;
}

const useVideoCompress = () => {
	const ffmpegRef = useRef<FFmpeg | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const [isFFmpegLoaded, setIsFFmpegLoaded] = useState(false);

	const initFFmpeg = useCallback(async () => {
		if (ffmpegRef?.current && isFFmpegLoaded) return;

		try {
			const ffmpeg = new FFmpeg();

			const baseURL = '/ffmpeg';

			const coreURL = await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript');
			const wasmURL = await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm');
			const workerURL = await toBlobURL(`${baseURL}/ffmpeg-core.worker.js`, 'text/javascript');

			await ffmpeg.load({
				coreURL,
				wasmURL,
				workerURL
			});

			ffmpegRef.current = ffmpeg;
			setIsFFmpegLoaded(true);
		} catch (err) {
			console.error('FFmpeg yükleme hatası:', err);

			const errorMsg = err instanceof Error ? err.message : 'FFmpeg başlatılamadı';
			setError(errorMsg);
			throw err;
		}
	}, [isFFmpegLoaded]);

	const cleanupFiles = useCallback(async (ffmpeg: FFmpeg, files: string[]) => {
		for (const file of files) {
			try {
				await ffmpeg.deleteFile(file);
			} catch (err) {
				console.warn(`Dosya silinemedi: ${file}`, err);
			}
		}
	}, []);

	const compressVideo = useCallback(
		async (videoFile: File): Promise<CompressResult> => {
			setIsLoading(true);
			setProgress(0);
			setError(null);

			const inputPath = 'input.webm';
			const outputPath = 'output.webm';
			const tempFiles: string[] = [inputPath, outputPath];

			try {
				if (!ffmpegRef.current || !isFFmpegLoaded) {
					setProgress(10);
					await initFFmpeg();
					setProgress(20);
				}

				const ffmpeg = ffmpegRef.current!;

				setProgress(30);
				await ffmpeg.writeFile(inputPath, await fetchFile(videoFile));

				setProgress(40);

				await ffmpeg.exec([
					'-i',
					inputPath,
					'-c:v',
					'libvpx',
					'-crf',
					'10',
					'-b:v',
					'1M',
					'-c:a',
					'libopus',
					'-b:a',
					'128k',
					'-pix_fmt',
					'yuv420p',
					'-movflags',
					'+faststart',
					'-cpu-used',
					'4',
					'-deadline',
					'realtime',
					outputPath
				]);

				setProgress(80);
				const data = await ffmpeg.readFile(outputPath);
				const resultArray = data as Uint8Array;

				setProgress(90);
				const blob = new Blob([resultArray], { type: 'video/webm' });
				const url = URL.createObjectURL(blob);

				await cleanupFiles(ffmpeg, tempFiles);

				setProgress(100);
				setIsLoading(false);

				return {
					data: resultArray,
					blob,
					url
				};
			} catch (err) {
				console.error('Video sıkıştırma hatası:', err);

				if (ffmpegRef.current) {
					try {
						await cleanupFiles(ffmpegRef.current, tempFiles);
					} catch (cleanupErr) {
						console.warn('Cleanup hatası:', cleanupErr);
					}
				}

				const errorMessage = err instanceof Error ? err.message : 'Video sıkıştırma başarısız oldu';
				setError(errorMessage);
				setIsLoading(false);
				throw new Error(errorMessage);
			}
		},
		[initFFmpeg, cleanupFiles, isFFmpegLoaded]
	);

	return {
		compressVideo,
		isLoading,
		progress,
		error,
		isFFmpegLoaded
	};
};

export default useVideoCompress;
